import { Drawer } from '@material-ui/core';
import { cloneDeep } from 'lodash';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Button } from '../../components/button/Button';
import { FlyoutFooter } from '../../components/flyout-components/FlyoutFooter';
import { TextInput } from '../../components/form-elements/_index';
import { ShipmentMethod } from '../../transportation/models/enums/shipment-method';
import { StatusCode } from '../../utils/api';
import { CreateProductionRunPickup, OrderGroupChoice } from '../models/metrics/create-production-run-pickup';
import { UnplannedPickupMetricsGroup } from '../models/metrics/unplanned-pickup-metrics-group';
import { ProductionSvc } from '../service';

interface IProps {
    isOpen: boolean;
    onClose: any;
    taskGroups: any[];
    maxCutoff: string | undefined;
    division: string | undefined;
    shipmentMethod?: string | undefined;
    isUrgent: boolean;
}

export const CreateRunForPickupFlyout = (props : IProps) => {

    const [service] = React.useState(new ProductionSvc());
    const [blockClose, setBlockClose] = React.useState(false);
    const [taskCopy, setTaskCopy] = React.useState<UnplannedPickupMetricsGroup[]>([]);
    const [wasSuccess, setWasSuccess] = React.useState(false);
    const [madeAttempt, setMadeAttempt] = React.useState(false);
    const [name, setName] = React.useState('');
    const [maxCutoff, setMaxCutoff] = React.useState<Date | undefined>(undefined);
    const [division, setDivision] = React.useState<string | undefined>(undefined);
    const [shipmentMethod, setShipmentMethod] = React.useState<string | undefined>(undefined);

    /**
     * When isOpen toggles, refresh data
     */
    React.useEffect(() => {
        if (props.isOpen) {
            const t = cloneDeep(props.taskGroups);
            setTaskCopy(t);
            setName('');
            setWasSuccess(false);
            setMadeAttempt(false);
            setBlockClose(false);            

            if (props.maxCutoff) {
                const maxDate = props.maxCutoff.split('&')[0];
                const asDate = new Date(maxDate + 'T12:00:00.000Z');
                setMaxCutoff(asDate);
            }

            setDivision(props.division);
            setShipmentMethod(props.shipmentMethod);
             
            if (props.isUrgent) {
                setMaxCutoff(undefined);
            }

        }
    }, [props.isOpen]);

    /**
     * Fire our request to create a production run for these pickups
     */
    const createRuns = async () => {
        setBlockClose(true);
        setMadeAttempt(true);

        const req = new CreateProductionRunPickup();
        req.name = name;
        req.division = division?.toString(); // for some reason it ends up being a number in javascript
        req.shipmentMethod = shipmentMethod?.toString();
        req.maxCutoff = maxCutoff;
        req.isUrgent = props.isUrgent;
        req.dealerIds = taskCopy
            .filter((current : UnplannedPickupMetricsGroup) => current.shipmentMethod !== ShipmentMethod.Delivery)
            .map((current : UnplannedPickupMetricsGroup) => current.dealerId);
        req.groups = taskCopy.map((current : UnplannedPickupMetricsGroup) => {
            const choice = new OrderGroupChoice();
            choice.dealerId = current.dealerId;
            choice.shipToAddressId = current.shipmentMethod === ShipmentMethod.Delivery ? current.shipToAddressId : undefined;
            choice.groupId = current.groupId;
            return choice;
        });
        req.shipToAddressIds = taskCopy
            .filter((current : UnplannedPickupMetricsGroup) => current.shipmentMethod === ShipmentMethod.Delivery)
            .map((current : UnplannedPickupMetricsGroup) => current.shipToAddressId!);

        const result = await service.createProductionRunForPickup(req);

        (result.status === StatusCode.Success)
            ? setWasSuccess(true)
            : setWasSuccess(false);
                
        setBlockClose(false);
    };

    const closeFlyout = () => {
        props.onClose(madeAttempt);
    };

    /**
     * Handler for changing the name
     * @param e The text input event 
     */
    const onNameChange = (e : any) => {
        if (e && e.target && e.target.value !== undefined) {
            setName(e.target.value);
        }        
    };

    return (
        <Drawer
            anchor="right"
            onClose={ closeFlyout }
            open={props.isOpen}>
            <div className="stop-action-drawer stop-action-reorder-route production-module-flyout">
                <div className="row">
                    <div className="col">
                        <h1>Create Production Runs</h1>
                    </div>
                </div>

                {
                    props.isUrgent
                        ? <div className="row">
                            <div className="col">
                                <h3 className="production-planning-important-create">Urgent</h3>
                            </div>
                        </div>
                        : null
                }
                    
                <div className="row">
                    <div className="col">
                        <p>Confirm your selections and press the 'Create' button to create a new production run for these dealer pickups and address deliveries.</p>
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <TextInput
                            id="planning-run-name"
                            type="text"
                            label="Name"
                            name="name"
                            value={ name }
                            onChange={ onNameChange }
                        />
                    </div>
                </div>

                <div className="row prod-planning-dealer-list">
                    <div className="col">
                        <h3>Pickups and Deliveries</h3>
                        {
                            taskCopy
                                ? taskCopy.map((itm : UnplannedPickupMetricsGroup, idx: number) => 
                                    <div className="dealer-itm" key={idx}>
                                        <span>{itm.name}</span>
                                        <span>{itm.dealerId}</span>
                                    </div>
                                )
                                : <div>No Tasks Selected.<br/>How did you even get here?</div>
                        }            
                    </div>
                </div>  
                         
                <CSSTransition
                    in={ wasSuccess }
                    timeout={300}
                    classNames="productionOrderMenu"
                    unmountOnExit>
                        <div className="row">
                            <div className="col create-success-message">
                                <p>All runs have been created.  You can head to the scheduling page to view them.</p>
                                <br/>
                                <i className="la la-check-circle" />
                            </div>
                        </div>
                </CSSTransition>
               
                <FlyoutFooter>
                    <div className="row">
                        <div className="col">                            
                            {
                                wasSuccess
                                    ? <Button
                                        text="Close"
                                        classes="action"
                                        testID="close-planning-flyout"
                                        onBtnClick={ closeFlyout } />
                                    : <Button
                                        text="Create Runs"
                                        classes="attention"
                                        testID="create-planning-flyout"
                                        onBtnClick={ createRuns } />
                            }                            
                        </div>
                    </div>
                </FlyoutFooter>                    

            </div>
        </Drawer>
    );

};
